import React from 'react';
import { Alert, Content, HelperText, HelperTextItem } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import type { DomainResourceElement } from './domainResourceTypes';
import type { ResourceStepProps } from './resourceStep';

const KIND_LABEL: Record<string, string> = { mailbox: 'Mailbox', alias: 'Alias' };

/**
 * Email-hosting step: pick which mailboxes and aliases go.
 *
 * The mail-domain **record** is not a row here — it is derived: it goes exactly when every deletable
 * mailbox and alias is selected, because a kept mailbox needs its domain (plan decision 3(c)). The
 * consequence of the current selection is spelled out under the table.
 *
 * Rows are one table (not one per kind) on purpose: `@patternfly/react-table` generates its checkbox
 * ids from the row index, so two selectable tables on one page would collide.
 */
const EmailHostingStep: React.FunctionComponent<ResourceStepProps> = ({
  domain,
  resources,
  elements,
  selectedKeys,
  onToggle,
  onToggleAll,
  dkim,
  dkimLoading,
  mailDomainGoes,
}) => {
  const email = resources.email_hosting;
  const rows: readonly DomainResourceElement[] = elements.filter((element) => !element.derived);
  const record = elements.find((element) => element.derived) ?? null;
  const selectableRows = rows.filter((element) => element.deletable);
  const selectedRows = selectableRows.filter((element) => selectedKeys.has(element.key));
  const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;
  const keptCount = selectableRows.length - selectedRows.length;

  const dkimNote = dkimLoading
    ? ' DKIM keys: counting…'
    : dkim !== null && dkim.count > 0
      ? ` Its ${dkim.count} DKIM ${dkim.count === 1 ? 'key goes' : 'keys go'} with it.`
      : '';

  return (
    <div>
      <Content component="p" className="pf-v6-u-mb-md">
        These mailboxes and aliases are hosted for <strong>{domain.name}</strong>. Everything you leave unticked is
        kept, together with the mail domain it needs.
      </Content>

      {email !== null && email.host_state === 'stale_binding' && (
        <Alert
          variant="info"
          isInline
          component="h2"
          title="Nothing is left on the mail host"
          className="pf-v6-u-mb-md"
        >
          <p>{email.host_error ?? 'The domain is no longer provisioned on its mail host.'}</p>
        </Alert>
      )}

      {email !== null && email.host_state === 'unreachable' && (
        <Alert
          variant="warning"
          isInline
          component="h2"
          title="The mail host could not be reached"
          className="pf-v6-u-mb-md"
        >
          <p>
            {email.host_error ?? 'The mail host cannot be reached; try again later.'} Its mailboxes and aliases cannot
            be listed, so nothing here can be deleted yet.
          </p>
        </Alert>
      )}

      {rows.length > 0 && (
        <Table aria-label={`Mailboxes and aliases of ${domain.name}`} variant="compact">
          <Thead>
            <Tr>
              <Th
                select={{
                  onSelect: (_event, isSelecting) => onToggleAll(isSelecting),
                  isSelected: allSelected,
                  isIndeterminate: someSelected,
                  // `isHeaderSelectDisabled` is a no-op in react-table 6.6.0 — `isDisabled` is the one
                  // that reaches the input.
                  isDisabled: selectableRows.length === 0,
                }}
                aria-label="Select all mailboxes and aliases"
              />
              <Th>Address</Th>
              <Th>Type</Th>
              <Th>Details</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((element, rowIndex) => (
              <Tr key={element.key}>
                <Td
                  select={{
                    rowIndex,
                    isSelected: selectedKeys.has(element.key),
                    isDisabled: !element.deletable,
                    onSelect: (_event, isSelecting) => onToggle(element.key, isSelecting),
                  }}
                />
                <Td dataLabel="Address">
                  {element.label}
                  {/* A disabled row checkbox cannot carry a tooltip (react-table hard-disables the
                      input), so the reason is visible text. */}
                  {element.blockedReason !== null && (
                    <HelperText>
                      <HelperTextItem>{element.blockedReason}</HelperTextItem>
                    </HelperText>
                  )}
                </Td>
                <Td dataLabel="Type">{KIND_LABEL[element.kind] ?? element.kind}</Td>
                <Td dataLabel="Details">{element.detail ?? '—'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {rows.length === 0 && (
        <Content component="p">
          This domain has no mailboxes and no aliases. Only its mail-domain record is left to remove.
        </Content>
      )}

      <HelperText className="pf-v6-u-mt-md">
        <HelperTextItem>
          {record === null
            ? 'No mail domain is hosted for this domain.'
            : mailDomainGoes
              ? `Everything is selected, so the mail domain record for ${domain.name} is removed too, with its IMAP binding and antispam link.${dkimNote}`
              : `Keeping ${keptCount} ${keptCount === 1 ? 'item' : 'items'} keeps the mail domain record for ${domain.name}, and therefore the organization domain.`}
        </HelperTextItem>
      </HelperText>

      <Content component="p" className="pf-v6-u-mt-sm pf-v6-u-text-color-subtle pf-v6-u-font-size-sm">
        Deleting a mailbox is irreversible, including unread mail. Message files stay on the mail host until its own
        cleanup removes them, and no credit is issued for a prepaid period.
      </Content>
    </div>
  );
};

export default EmailHostingStep;
